// Evolución por semana de un paciente: ROM (rango de movimiento) y dolor,
// derivados de las sesiones completadas de las últimas semanas.
//
// - ROM de una sesión = promedio de `prominencia_promedio_grados` de sus
//   series en pose_analysis (amplitud real del movimiento, ver
//   buildPoseAnalysis). Se toma el ejercicio con más sesiones medidas para
//   no mezclar escalas (grados de hombro vs rodilla).
// - Dolor = promedio semanal de `dolor_reportado` (0-10).

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase/client";

export interface PuntoSemana {
  /** "S1".."SN", de la más vieja a la más reciente. */
  week: string;
  value: number;
}

export interface RomPaciente {
  ejercicioNombre: string;
  semanas: PuntoSemana[];
  /** Grados ganados: última semana menos primera semana. */
  ganado: number;
}

export interface EvolucionPaciente {
  rom: RomPaciente | null;
  /** Vacío si no hay dolor reportado en la ventana. */
  dolorSemanas: PuntoSemana[];
}

export interface SesionEvolucionRow {
  started_at: string;
  ejercicio_id: string;
  dolor_reportado: number | null;
  pose_analysis: {
    ejecucion?: { series?: Array<{ prominencia_promedio_grados?: number }> };
  } | null;
  ejercicios: { nombre: string } | null;
}

const MAX_SEMANAS = 6;
const WEEK_MS = 7 * 86_400_000;

function romDeSesion(row: SesionEvolucionRow): number | null {
  const vals = (row.pose_analysis?.ejecucion?.series ?? [])
    .map((s) => s.prominencia_promedio_grados ?? 0)
    .filter((v) => v > 0);
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/** Agrupa valores en semanas hacia atrás desde hoy y devuelve el promedio
 *  de cada semana con datos, de la más vieja a la más reciente. */
function porSemana(
  items: Array<{ started_at: string; value: number }>,
  decimales = 0,
): PuntoSemana[] {
  const now = Date.now();
  const buckets = new Map<number, number[]>();
  for (const { started_at, value } of items) {
    const idx = Math.floor((now - new Date(started_at).getTime()) / WEEK_MS);
    if (idx >= MAX_SEMANAS) continue;
    buckets.set(idx, [...(buckets.get(idx) ?? []), value]);
  }
  const f = 10 ** decimales;
  return [...buckets.keys()]
    .sort((a, b) => b - a)
    .map((idx, i) => {
      const vals = buckets.get(idx)!;
      const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
      return { week: `S${i + 1}`, value: Math.round(avg * f) / f };
    });
}

export function calcularEvolucion(rows: SesionEvolucionRow[]): EvolucionPaciente {
  const dolorSemanas = porSemana(
    rows
      .filter((r) => r.dolor_reportado !== null)
      .map((r) => ({ started_at: r.started_at, value: r.dolor_reportado! })),
    1,
  );

  const conRom = rows
    .map((row) => ({ row, rom: romDeSesion(row) }))
    .filter((x): x is { row: SesionEvolucionRow; rom: number } => x.rom !== null);
  if (conRom.length === 0) return { rom: null, dolorSemanas };

  const counts = new Map<string, number>();
  for (const { row } of conRom) {
    counts.set(row.ejercicio_id, (counts.get(row.ejercicio_id) ?? 0) + 1);
  }
  const ejercicioId = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
  const delEjercicio = conRom.filter((x) => x.row.ejercicio_id === ejercicioId);
  const semanas = porSemana(
    delEjercicio.map((x) => ({ started_at: x.row.started_at, value: x.rom })),
  );
  if (semanas.length === 0) return { rom: null, dolorSemanas };

  return {
    rom: {
      ejercicioNombre: delEjercicio[0].row.ejercicios?.nombre ?? ejercicioId,
      semanas,
      ganado: semanas[semanas.length - 1].value - semanas[0].value,
    },
    dolorSemanas,
  };
}

export function useEvolucionPaciente(pacienteId: string | null): {
  data: EvolucionPaciente | null;
  loading: boolean;
} {
  const [state, setState] = useState<{
    data: EvolucionPaciente | null;
    loading: boolean;
  }>({ data: null, loading: !!pacienteId });

  useEffect(() => {
    if (!pacienteId) {
      setState({ data: null, loading: false });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    void (async () => {
      const { data } = await supabase
        .from("sesiones")
        .select("started_at, ejercicio_id, dolor_reportado, pose_analysis, ejercicios(nombre)")
        .eq("paciente_id", pacienteId)
        .eq("estado", "completada")
        .gte("started_at", new Date(Date.now() - MAX_SEMANAS * WEEK_MS).toISOString());
      if (cancelled) return;
      setState({
        data: calcularEvolucion((data ?? []) as unknown as SesionEvolucionRow[]),
        loading: false,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [pacienteId]);

  return state;
}
